import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from '../utils/axiosInstance';
import { BASE_URL } from '@/config/constants';
import { toast } from 'sonner';
import { Globe, ExternalLink, Copy, Check, ArrowRight } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';

/**
 * Global "Connect Custom Domain" modal.
 *
 * Flow:
 *   STEP 1 → user enters a domain → PUT /api/domain/:id  (saves customDomain)
 *                                  → POST /api/domain/attach-vercel  (best-effort)
 *   STEP 2 → DNS instructions
 *   STEP 3 → success / open website
 *
 * Triggered globally via PlanContext: `openCustomDomainModal()`
 * Also reusable inline (e.g. from the Custom Domain settings tab) by
 * passing `open` / `onOpenChange` / `domainId` props directly.
 */

interface CustomDomainModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Mongo `_id` of the Domain document being edited. */
  domainId: string;
  /** Optional pre-existing customDomain — if present, modal opens at STEP 2. */
  initialCustomDomain?: string | null;
  /** Called after a successful save so the parent can refresh. */
  onSaved?: (customDomain: string) => void;
  /** Called when DNS verification passes so the parent can update its state. */
  onVerified?: () => void;
}

const DNS_RECORDS: Array<{ type: 'A' | 'CNAME'; host: string; value: string }> = [
  { type: 'A', host: '@', value: '76.76.21.21' },
  { type: 'CNAME', host: 'www', value: '6fc403ba9e2df09f.vercel-dns-017.com.' },
];

const CustomDomainModal: React.FC<CustomDomainModalProps> = ({
  open,
  onOpenChange,
  domainId,
  initialCustomDomain,
  onSaved,
  onVerified,
}) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [domainInput, setDomainInput] = useState('');
  const [savedDomain, setSavedDomain] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [dnsError, setDnsError] = useState<string | null>(null);
  const [dnsRecords, setDnsRecords] = useState(DNS_RECORDS);

  // Reset/seed state when the modal opens
  useEffect(() => {
    if (!open) return;
    if (initialCustomDomain) {
      setSavedDomain(initialCustomDomain);
      setDomainInput(initialCustomDomain);
      setStep(2);
    } else {
      setSavedDomain('');
      setDomainInput('');
      setStep(1);
    }
    setSubmitting(false);
    setDnsError(null);
  }, [open, initialCustomDomain]);

  // Fetch current recommended DNS records from the backend when the modal opens.
  // Falls back to the hardcoded constant if the request fails.
  useEffect(() => {
    if (!open) return;
    axios
      .get(`${BASE_URL}/api/domain/dns-config`)
      .then((r) => {
        if (Array.isArray(r.data?.records) && r.data.records.length > 0) {
          setDnsRecords(r.data.records);
        }
      })
      .catch(() => {
        /* keep hardcoded fallback */
      });
  }, [open]);

  const websiteUrl = useMemo(
    () => (savedDomain ? `https://${savedDomain}` : ''),
    [savedDomain],
  );

  // Register the domain (apex + www) with the Vercel project so it actually
  // starts routing and provisioning SSL once DNS points at it. Best-effort &
  // non-blocking: the endpoint is idempotent (an already-attached domain is
  // treated as success) and verify-dns re-attaches on success too, so any
  // transient failure here is harmless — DNS instructions are still shown.
  const attachToVercel = (domain: string) => {
    if (!domain) return;
    axios
      .post(`${BASE_URL}/api/domain/attach-vercel`, { domain })
      .catch(() => {
        /* swallow — non-fatal, verify-dns will retry the attach */
      });
  };

  const handleSave = async () => {
    const value = domainInput.trim();
    if (!value) {
      toast.error('Please enter a domain.');
      return;
    }
    if (!domainId) {
      toast.error('No domain selected.');
      return;
    }

    // If the user re-submitted the exact domain that's already saved, skip the
    // PUT entirely — the backend's uniqueness check would 409 on its own record
    // on stale deployments, and the API call is redundant anyway.
    if (value.toLowerCase() === initialCustomDomain?.toLowerCase()) {
      const stored = value.toLowerCase();
      setSavedDomain(stored);
      attachToVercel(stored);
      setStep(2);
      return;
    }

    setSubmitting(true);
    try {
      // 1. Persist customDomain on the Domain document.
      const res = await axios.put(`${BASE_URL}/api/domain/${domainId}`, {
        customDomain: value,
      });
      const stored = (res.data && res.data.customDomain) || value.toLowerCase();
      setSavedDomain(stored);

      // 2. Register it with Vercel so it routes + provisions SSL. Without this
      // the domain is never known to Vercel and the DNS records never resolve
      // to a served site.
      attachToVercel(stored);

      onSaved?.(stored);
      toast.success('Domain saved. Configure DNS to finish.');
      setStep(2);
    } catch (err: any) {
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
      if (status === 409 || code === 'CUSTOM_DOMAIN_TAKEN') {
        toast.error('This domain is already taken by another artist.');
      } else if (status === 400) {
        toast.error(
          err?.response?.data?.message ||
            'Invalid domain. Use a format like "example.com".',
        );
      } else {
        toast.error('Failed to save domain. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyDns = async () => {
    setVerifying(true);
    setDnsError(null);
    try {
      const res = await axios.get(`${BASE_URL}/api/domain/${domainId}/verify-dns`, {
        params: { _t: Date.now() },
      });
      if (res.data?.verified) {
        setStep(3);
        onVerified?.();
      } else {
        setDnsError('DNS records not detected yet. It can take a few minutes to propagate — check your registrar settings and try again.');
      }
    } catch {
      setDnsError('Could not verify DNS. Please check your connection and try again.');
    } finally {
      setVerifying(false);
    }
  };

  const copy = (text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied((k) => (k === key ? null : k)), 1200);
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="size-5 text-primary" />
            Connect a custom domain
          </DialogTitle>
          <DialogDescription>
            {step === 1 && 'Use your own domain instead of the default one.'}
            {step === 2 && 'Add these DNS records at your domain registrar.'}
            {step === 3 && 'Your domain is connected.'}
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 my-2">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-1.5 w-10 rounded-full transition-colors ${
                step >= s ? 'bg-primary' : 'bg-muted'
              }`}
            />
          ))}
        </div>

        {/* ── STEP 1: input ──────────────────────────────────────── */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="custom-domain-input">Your domain</Label>
              <Input
                id="custom-domain-input"
                type="text"
                placeholder="yourname.com"
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                autoComplete="off"
                disabled={submitting}
              />
              <p className="text-xs text-muted-foreground">
                Enter the apex domain you own (without "https://" or "www.").
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={submitting || !domainInput.trim()}>
                {submitting ? (
                  <>
                    <Spinner size="sm" className="text-inherit" /> Saving…
                  </>
                ) : (
                  <>
                    Continue <ArrowRight className="size-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 2: DNS instructions ───────────────────────────── */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="rounded-md border bg-muted/30 p-3 text-sm">
              Adding records for{' '}
              <span className="font-medium text-foreground">{savedDomain}</span>
            </div>

            <div className="overflow-hidden rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Type</th>
                    <th className="px-3 py-2 text-left font-medium">Host</th>
                    <th className="px-3 py-2 text-left font-medium">Value</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {dnsRecords.map((r) => (
                    <tr key={`${r.type}-${r.host}`} className="border-t">
                      <td className="px-3 py-2 font-mono">{r.type}</td>
                      <td className="px-3 py-2 font-mono">{r.host}</td>
                      <td className="px-3 py-2 font-mono break-all">{r.value}</td>
                      <td className="px-3 py-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copy(r.value, `${r.type}-${r.host}`)}
                          aria-label="Copy value"
                        >
                          {copied === `${r.type}-${r.host}` ? (
                            <Check className="size-3.5" />
                          ) : (
                            <Copy className="size-3.5" />
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-muted-foreground">
              DNS changes can take a few minutes (sometimes up to 24 hours) to take effect.
            </p>

            {dnsError && (
              <p className="text-sm text-destructive rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2">
                {dnsError}
              </p>
            )}

            <div className="flex justify-between gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => setStep(1)}
                disabled={verifying}
              >
                Back
              </Button>
              <Button onClick={handleVerifyDns} disabled={verifying}>
                {verifying ? (
                  <>
                    <Spinner size="sm" className="text-inherit" /> Checking DNS…
                  </>
                ) : dnsError ? (
                  <>
                    Check Again <ArrowRight className="size-4" />
                  </>
                ) : (
                  <>
                    I&apos;ve added the records <ArrowRight className="size-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 3: success ────────────────────────────────────── */}
        {step === 3 && (
          <div className="space-y-4 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Check className="size-6 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Your domain is connected</h3>
              <p className="text-sm text-muted-foreground">
                It may take a few minutes for{' '}
                <span className="font-medium text-foreground">{savedDomain}</span> to start
                serving your site.
              </p>
            </div>
            <div className="flex justify-center gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              {websiteUrl && (
                <Button asChild>
                  <a href={websiteUrl} target="_blank" rel="noopener noreferrer">
                    Open Website <ExternalLink className="size-4" />
                  </a>
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CustomDomainModal;
